import React, { useEffect, useState } from 'react';
import { getBot, BotType } from '@/Data/Bot';
import FriendCard from '@/Components/FriendCard';
import LoadingView from '@/Components/social/LoadingView';
import ChatIcon from '@mui/icons-material/Chat';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { navigateTo, stripLanguagePrefix } from '@/Utils/routing';
import './botProfile.scss';
import { tLoader } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';

const translations = {
    en: {
        "Bot not found": "Bot not found",
        "Go to Chat": "Go to Chat",
        "years old": "years old",
        "Personality": "Personality",
        "Hobbies": "Hobbies",
        "Chat with": "Chat with"
    },
    id: {
        "Bot not found": "Bot tidak ditemukan",
        "Go to Chat": "Ke Chat",
        "years old": "tahun",
        "Personality": "Kepribadian",
        "Hobbies": "Hobi",
        "Chat with": "Ngobrol dengan"
    },
    ko: {
        "Bot not found": "봇을 찾을 수 없습니다",
        "Go to Chat": "채팅으로 가기",
        "years old": "세",
        "Personality": "성격",
        "Hobbies": "취미",
        "Chat with": "와 채팅하기"
    },
    vn: {
        "Bot not found": "Không tìm thấy bot",
        "Go to Chat": "Đi đến Chat",
        "years old": "tuổi",
        "Personality": "Tính cách",
        "Hobbies": "Sở thích",
        "Chat with": "Trò chuyện với"
    }
};

const APP_STORE_LINK = 'https://apps.apple.com/app/make-korean-friend/id6499510702';
const PLAY_STORE_LINK = 'https://play.google.com/store/apps/details?id=com.sugeul.makekoreanfriend';

const BotProfile = () => {
    const [, forceUpdate] = useState({});
    const t = tLoader(translations);

    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);

    // Get botid from URL path (after stripping language prefix)
    const strippedPath = stripLanguagePrefix(window.location.pathname);
    const pathParts = strippedPath.split('/');
    const botid = pathParts[pathParts.length - 1];

    const [bot, setBot] = useState<BotType | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadBot = async () => {
            if (!botid) { return; }
            setLoading(true);
            const botData = await getBot(botid);
            setBot(botData);
            setLoading(false);
        };
        loadBot();
    }, [botid]);

    const handleChatNow = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        if (/iphone|ipad|ipod/.test(userAgent)) {
            window.location.href = APP_STORE_LINK;
        } else {
            window.location.href = PLAY_STORE_LINK;
        }
    };

    if (loading) {
        return <LoadingView />;
    }

    if (!bot) {
        return (
            <div className="BotProfilePage">
                <div className="NotFound">
                    <h2>{t("Bot not found")}</h2>
                    <button onClick={() => navigateTo('/chat')}>{t("Go to Chat")}</button>
                </div>
            </div>
        );
    }

    return (
        <div className="BotProfilePage">
            {/* Gradient background */}
            <div className="GradientBg" />

            {/* Back button */}
            <button className="BackButton" onClick={() => navigateTo('/chat')}>
                <ArrowBackIcon />
            </button>

            {/* Card container */}
            <div className="CardContainer">
                <FriendCard friend={bot} staticMode />
            </div>

            {/* Bot info */}
            <div className="BotInfo">
                <h1>{bot.name}</h1>
                <p className="Origin">{bot.origin} · {bot.age} {t("years old")}</p>
                <p className="Description">{bot.description}</p>

                {/* Tags */}
                <div className="TagsSection">
                    {bot.personality?.length > 0 && (
                        <div className="TagGroup">
                            <h4>{t("Personality")}</h4>
                            <div className="Tags">
                                {bot.personality.map(tag => (
                                    <span key={tag} className="Tag">{tag}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {bot.hobby?.length > 0 && (
                        <div className="TagGroup">
                            <h4>{t("Hobbies")}</h4>
                            <div className="Tags">
                                {bot.hobby.map(tag => (
                                    <span key={tag} className="Tag">{tag}</span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* CTA button */}
            <div className="CTASection">
                <button className="ChatButton" onClick={handleChatNow}>
                    <ChatIcon /> {t("Chat with")} {bot.name}
                </button>
            </div>
        </div>
    );
};

export default BotProfile;
